import { bagCouponFocus, detachBagCouponCode, type BagCoupon } from "@/lib/bag-coupon"
import { getMerchantId } from "@/lib/company-storage"
import { VARIANTS } from "@/lib/motion"
import { AnimatePresence, motion } from "motion/react"
import { useLocation, useNavigate } from "react-router-dom"
import { BagCouponCard } from "./BagCouponCard"
import { BagCouponForm } from "./BagCouponForm"
import { CouponAddRow } from "./CouponAddRow"
import { CouponGroupCard } from "./CouponGroupCard"

/** URL hash that opens the bag on the coupon strip (the bag pill links to `/cart#cupom`). */
export const BAG_COUPON_HASH = "#cupom"

function focusOnArrival(node: HTMLElement | null) {
  node?.focus({ preventScroll: true })
}

function remove(code: string) {
  const merchantId = getMerchantId()
  if (merchantId) detachBagCouponCode(merchantId, code)
}

/**
 * The coupon on this order, right above the bag lines: ONE ticket — the
 * applied coupon (or "2 cupons" when they combine), else the one closest to
 * applying — or the "Tenho um cupom" row when there is none. Codes that do
 * not ride on the order leave on their own (see `useBagCoupons`); prizes stay
 * in CartPrizes.
 */
export function BagCouponStrip({ coupons, ready }: { coupons: readonly BagCoupon[]; ready: boolean }) {
  const { hash } = useLocation()
  const navigate = useNavigate()
  if (!ready) return null
  const focus = bagCouponFocus(coupons)

  return (
    <section
      id={BAG_COUPON_HASH.slice(1)}
      aria-label="Cupom do pedido"
      tabIndex={-1}
      ref={hash === BAG_COUPON_HASH ? focusOnArrival : undefined}
      className="mb-3 scroll-mt-24 outline-none"
    >
      <AnimatePresence mode="wait" initial={false}>
        {focus ? (
          <motion.ul key="coupon" variants={VARIANTS.fadeUp} initial="hidden" animate="visible" exit="exit">
            {focus.kind === "group" ? (
              <CouponGroupCard coupons={focus.coupons} discountCents={focus.discountCents} onRemove={remove} />
            ) : (
              <BagCouponCard
                coupon={focus.coupon}
                onRemove={() => remove(focus.coupon.code)}
                onAddItems={() => navigate("/")}
                replaceForm={(close) => <BagCouponForm replacing={focus.coupon.code} onDone={close} onCancel={close} />}
              />
            )}
          </motion.ul>
        ) : (
          <motion.div key="add" variants={VARIANTS.fadeUp} initial="hidden" animate="visible" exit="exit">
            <CouponAddRow form={(close) => <BagCouponForm onDone={close} onCancel={close} />} />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  )
}
